import React, {useEffect, useState} from 'react'
import SockJS from 'sockjs-client'
import {over} from 'stompjs'
import Api from '../../services/api'
import {MODERATOR_SERVICE_BASE_URL} from '../../services/environments'
import {isAuthenticated} from '../../utilities'
import ModeratorQuestionDrawer from './components/ModeratorQuestionDrawer'
import ModeratorFullPageAd from './components/ModeratorFullPageAd'
import SectionSummaryDrawer from './components/SectionSummaryDrawer'
import UserConferenceSummary from './components/UserConferenceSummary'
import LeaderBoardDrawer from './components/LeaderBoardDrawer'
import '../../assets/scss/main.scss'

let stompClient = null
const USER_TYPE = 'user'

function ModeratorUserSocket({projectId, setOpenErrorModal}) {
    const [questionData, setQuestionData] = useState({})
    const [userClosestMatch, setUserClosestMatch] = useState([])
    const [leaderBoardDetails, setLeaderBoardDetails] = useState(null)
    const [openQuestionModal, setOpenQuestionModal] = useState(false)
    const [closingCount, setClosingCount] = useState(5)
    const [initClosing, setInitClosing] = useState(false)
    const [isQuestionSubmitted, setIsQuestionSubmitted] = useState(false)
    const [showingResult, setShowingResult] = useState(false)
    const [questionResult, setQuestionResult] = useState({})
    const [selectedData, setSelectedData] = useState({})
    const [fullPageAdData, setFullPageAdData] = useState({})
    const [showFullPageAd, setShowFullPageAd] = useState(false)
    const [sectionSummary, setSectionSummary] = useState({})
    const [openSectionSummaryModal, setOpenSectionSummaryModal] = useState(false)
    const [openUserConferenceSummary, setOpenUserConferenceSummary] = useState(false)
    const [openLeaderBoard, setOpenLeaderBoard] = useState(false)
    const [finalSummaryText, setFinalSummaryText] = useState('')

    useEffect(() => {
        return () => {
            if (stompClient) {
                stompClient.disconnect()
            }
        }
    }, [])

    useEffect(() => {
        const liveChat = document.getElementById("chat-widget-container");
        if (liveChat) {
            liveChat.style.display = "none";
        }
        return () => {
            if (liveChat) {
                liveChat.style.display = "unset";
            }
        };
    }, [document.getElementById("chat-widget-container")]);

    useEffect(() => {
        if (projectId && isAuthenticated()) {
            fetchActiveModeratorProjects()
        }
    }, [projectId, isAuthenticated()])

    useEffect(() => {
        if (initClosing && closingCount === 0) {
            handleCloseAll()
        }
    }, [initClosing, closingCount])

    useEffect(() => {
        let interval = null
        if (initClosing) {
            interval = setInterval(() => {
                setClosingCount((prevCount) => (prevCount > 0 ? prevCount - 1 : 5))
            }, 1000)
        }
        return () => {
            if (interval) {
                clearInterval(interval)
            }
        }
    }, [initClosing])

    const fetchActiveModeratorProjects = () => {
        Api.get('v1/moderator/getActiveProjects', MODERATOR_SERVICE_BASE_URL).then(response => {
            let isActiveProject = false
            response.moderatorProjects?.forEach(project => {
                if (project.id == projectId) {
                    isActiveProject = true
                    setFinalSummaryText(project.summaryText)
                }
            })
            if (isActiveProject) {
                connectToSocket()
                setOpenErrorModal && setOpenErrorModal(false)
            } else {
                setOpenErrorModal && setOpenErrorModal(true)
            }
        }).catch(error => {
            setOpenErrorModal && setOpenErrorModal(true)
            console.log(error)
        })
    }

    const connectToSocket = () => {
        let Sock = new SockJS(MODERATOR_SERVICE_BASE_URL + 'ws?projectId=' + projectId + '&accessToken=' + isAuthenticated())
        stompClient = over(Sock)
        stompClient.heartbeat.outgoing = 10000
        stompClient.heartbeat.incoming = 10000
        stompClient.connect({}, onConnected, onError)
        stompClient.ws.onclose = (event) => {
            if (event.code === 1002 || event.code === 1006) {
                console.error('Disconnected due to heartbeat failure')
                setTimeout(() => {
                    connectToSocket()
                }, 5000)
            } else {
                console.error('Disconnected from WebSocket with code:', event.code)
                setTimeout(() => {
                    connectToSocket()
                }, 5000)
            }
        }
    }

    const onConnected = () => {
        stompClient.subscribe(`/receiveQuestion/${USER_TYPE}/${projectId}`, onQuestionReceived)
        stompClient.subscribe(`/receiveQuestionResult/${USER_TYPE}/${projectId}`, onResultReceived)
        stompClient.subscribe(`/receiveSectionSummary/${USER_TYPE}/${projectId}`, onSectionSummaryReceived)
        stompClient.subscribe(`/receiveConferenceSummaryInit/${projectId}`, onConferenceSummaryReceived)
        stompClient.subscribe(`/receiveLeaderboard/${projectId}`, onLeaderBoardReceived)
        stompClient.subscribe(`/receiveBroadcast/${projectId}`, onBroadcastReceived)
        userJoin()
    }

    const onError = (err) => {
        console.log(err)
        setTimeout(() => {
            connectToSocket()
        }, 5000)
    }

    const onQuestionReceived = (payload) => {
        let parsedData = JSON.parse(payload.body)
        let question = parsedData.body?.question
        if (question) {
            setIsQuestionSubmitted(false)
            setShowingResult(false)
            setQuestionData(question)
            setOpenQuestionModal(true)
        }
    }

    const onResultReceived = (payload) => {
        let parsedData = JSON.parse(payload.body)
        let response = parsedData.body
        if (response) {
            setIsQuestionSubmitted(false)
            setQuestionData(response.question)
            setShowingResult(true)
            setOpenQuestionModal(true)
            setQuestionResult(response)
        }
    }

    const onSectionSummaryReceived = (payload) => {
        let parsedData = JSON.parse(payload.body)
        let response = parsedData.body
        if (response) {
            setIsQuestionSubmitted(false)
            setSectionSummary(response)
            setShowingResult(false)
            setOpenQuestionModal(false)
            setOpenSectionSummaryModal(true)
        }
    }

    const onConferenceSummaryReceived = (payload) => {
        let parsedData = JSON.parse(payload.body)
        let response = parsedData.body
        if (response) {
            getUserClosestMatch()
        }
    }

    const onLeaderBoardReceived = (payload) => {
        let parsedData = JSON.parse(payload.body)
        let response = parsedData.body
        if (response) {
            getLeaderBoardDetails()
        }
    }

    const onBroadcastReceived = (payload) => {
        let parsedData = JSON.parse(payload.body)
        if (parsedData?.type == 'close') {
            if (!initClosing) {
                handleCloseInit()
            }
        } else if (parsedData?.type == 'fullPageAd') {
            handleCloseAll(true)
            setFullPageAdData(parsedData.data)
            setShowFullPageAd(true)
        }
    }

    const userJoin = () => {
        let payload = {
            accessToken: isAuthenticated(),
        }
        stompClient.send(`/registerUser/${projectId}`, {}, JSON.stringify(payload))
    }

    const getUserClosestMatch = () => {
        Api.get(`v1/moderator/getUserClosestMatchUsers?moderatorProjectId=${projectId}`, MODERATOR_SERVICE_BASE_URL)
            .then((response) => {
                setUserClosestMatch(response.closestMatchUsers)
                setOpenUserConferenceSummary(true)
            })
            .catch((error) => console.log(error))
    }

    const getLeaderBoardDetails = () => {
        Api.get(`v1/moderator/getLeaderBoard?moderatorProjectId=${projectId}`, MODERATOR_SERVICE_BASE_URL)
            .then((response) => {
                setLeaderBoardDetails(response)
                setOpenLeaderBoard(true)
            })
            .catch((error) => console.log(error))
    }

    const handleCloseQuestionDrawer = () => {
        setOpenQuestionModal(false)

    }

    const handleCloseSectionSummaryDrawer = () => {
        setOpenSectionSummaryModal(false)

    }

    const handleCloseInit = () => {
        setInitClosing(true)
    }

    const handleClearData = () => {
        setClosingCount(5)
        setInitClosing(false)
        setShowingResult(false)
        setIsQuestionSubmitted(false)
        setSelectedData({})
        setQuestionData({})
        setSectionSummary({})
        setFullPageAdData({})
    }

    const handleCloseAll = (doNotClearData) => {
        handleCloseQuestionDrawer()
        handleCloseSectionSummaryDrawer()
        closeFullPageAd()
        setOpenUserConferenceSummary(false)
        setOpenLeaderBoard(false)
        if (!doNotClearData) {
            handleClearData()
        }
    }

    const submitResponseToSocket = (payload) => {
        stompClient.send(`/submitQuestionResponse/${projectId}`, {}, JSON.stringify(payload))
        setIsQuestionSubmitted(true)
    }

    const updateAdClickToSocket = (payload) => {
        stompClient.send(`/submitAdClick/${projectId}`, {}, JSON.stringify(payload))
    }

    const updateAdViewToSocket = (payload) => {
        stompClient.send(`/submitAdView/${projectId}`, {}, JSON.stringify(payload))
    }

    const closeFullPageAd = () => {
        setShowFullPageAd(false)
    }

    return projectId ? (
        <div className='flex fd-column pl-16 pr-16' style={{zIndex: '1000'}}>
            {showFullPageAd ? <ModeratorFullPageAd advData={fullPageAdData} closeAd={closeFullPageAd}
                                                   updateAdClickToSocket={updateAdClickToSocket}
                                                   updateAdViewToSocket={updateAdViewToSocket}/> : ''}
            <SectionSummaryDrawer open={openSectionSummaryModal} setOpen={handleCloseAll}
                                  sectionSummary={sectionSummary}/>
            <ModeratorQuestionDrawer
                visible={openQuestionModal}
                setVisible={handleCloseAll}
                questionData={questionData}
                submitResponseToSocket={submitResponseToSocket}
                isQuestionSubmitted={isQuestionSubmitted}
                closeText={initClosing ? `Closing in ${closingCount}s` : isQuestionSubmitted || showingResult ? 'Close' : 'Submit'}
                showingResult={showingResult}
                questionResult={questionResult}
                selectedData={selectedData}
                setSelectedData={setSelectedData}
                updateAdClickToSocket={updateAdClickToSocket}
                updateAdViewToSocket={updateAdViewToSocket}
            />
            <UserConferenceSummary open={openUserConferenceSummary} setOpen={handleCloseAll}
                                   userClosestMatch={userClosestMatch} finalSummaryText={finalSummaryText}/>
            <LeaderBoardDrawer open={openLeaderBoard} setOpen={handleCloseAll} data={leaderBoardDetails}/>
        </div>
    ) : ''
}

export default ModeratorUserSocket
